import React, { useState, useEffect } from 'react';
import { Plus, Calendar, MapPin, Clock, Trash2, ExternalLink, Lock } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { evenementAPI } from '../services/api';
import Toast from '../components/Toast';

const inp = { width:'100%', padding:'7px 11px', border:'1px solid #D1D5DB', borderRadius:6, fontSize:'0.875rem', outline:'none', boxSizing:'border-box', color:'#111827' };

const typeTag = {
  QSO:       { bg:'#DBEAFE', c:'#1D4ED8' },
  Stamm:     { bg:'#D1FAE5', c:'#065F46' },
  Concours:  { bg:'#FEE2E2', c:'#991B1B' },
  Assemblée: { bg:'#EDE9FE', c:'#6D28D9' },
  Autre:     { bg:'#F3F4F6', c:'#374151' },
};

const statusTag = {
  pending:   { bg:'#FEF9C3', c:'#713F12', label:'En attente' },
  published: { bg:'#D1FAE5', c:'#065F46', label:'Publié' },
  rejected:  { bg:'#FEE2E2', c:'#991B1B', label:'Refusé' },
  draft:     { bg:'#F3F4F6', c:'#6B7280', label:'Brouillon' },
};

const AgendaPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [evenements, setEvenements] = useState([]);
  const [myEvenements, setMyEvenements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showLoginBanner, setShowLoginBanner] = useState(false);
  const [toast, setToast] = useState(null);
  const [formData, setFormData] = useState({ titre:'', description:'', debut_date:'', debut_heure:'', fin_date:'', fin_heure:'', lieu:'', type:'QSO' });
  const [showMyEv, setShowMyEv] = useState(false);
  const [mapModal, setMapModal] = useState(null); // { lieu }
  const [selectedEv, setSelectedEv] = useState(null);

  useEffect(() => { loadEvenements(); }, [isAuthenticated]); // eslint-disable-line

  useEffect(() => {
    if (selectedEv || showModal || mapModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [selectedEv, showModal, mapModal, showMyEv]);

  const loadEvenements = async () => {
    try {
      const res = await evenementAPI.getAll();
      setEvenements(res.data);
      if (isAuthenticated) {
        const myRes = await evenementAPI.getMy();
        setMyEvenements(myRes.data);
      }
    } catch { showToast('Erreur lors du chargement', 'error'); }
    finally { setLoading(false); }
  };

  const showToast = (message, type = 'success') => setToast({ message, type });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        date_debut: formData.debut_date && formData.debut_heure ? `${formData.debut_date}T${formData.debut_heure}` : formData.debut_date,
        date_fin: formData.fin_date && formData.fin_heure ? `${formData.fin_date}T${formData.fin_heure}` : (formData.fin_date || null),
      };
      await evenementAPI.create(payload);
      showToast('Événement soumis au comité pour validation.');
      setShowModal(false);
      setFormData({ titre:'', description:'', debut_date:'', debut_heure:'', fin_date:'', fin_heure:'', lieu:'', type:'QSO' });
      loadEvenements();
    } catch (err) { showToast(err.response?.data?.error || 'Erreur', 'error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet événement ?')) return;
    try { await evenementAPI.delete(id); showToast('Supprimé.'); loadEvenements(); }
    catch (err) { showToast(err.response?.data?.error || 'Erreur', 'error'); }
  };

  if (loading) return (
    <div style={{display:'flex',justifyContent:'center',alignItems:'center',minHeight:300}}>
      <div style={{width:24,height:24,border:'2px solid #111827',borderTopColor:'transparent',borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/>
    </div>
  );

  const grouped = {};
  evenements.forEach(ev => {
    const key = format(new Date(ev.date_debut), 'MMMM yyyy', { locale: fr });
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(ev);
  });

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>

      {/* ── Toolbar ── */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
        <h2 style={{fontSize:'0.75rem',fontWeight:700,color:'#6B7280',textTransform:'uppercase',letterSpacing:'0.08em',margin:0}}>Agenda du club</h2>
        <button onClick={isAuthenticated ? ()=>setShowModal(true) : ()=>setShowLoginBanner(true)} style={{display:'flex',alignItems:'center',gap:5,backgroundColor:'#111827',color:'white',border:'none',borderRadius:6,padding:'6px 12px',fontSize:'0.8125rem',fontWeight:600,cursor:'pointer'}}>
          <Plus style={{width:13,height:13}}/> Proposer
        </button>
      </div>

      {/* ── Mes propositions (dropdown overlay) ── */}
      {isAuthenticated && (
        <div style={{position:'relative',zIndex:50}}>
          <button onClick={()=>setShowMyEv(v=>!v)} style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',padding:'9px 14px',backgroundColor:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:showMyEv?'10px 10px 0 0':10,cursor:'pointer',gap:8}}>
            <div style={{display:'flex',alignItems:'center',gap:7}}>
              <span style={{fontSize:'0.6875rem',fontWeight:700,color:'#1D4ED8',textTransform:'uppercase',letterSpacing:'0.06em'}}>Mes événements</span>
              {myEvenements.length > 0 && <span style={{backgroundColor:'#DBEAFE',color:'#1D4ED8',fontSize:'0.625rem',fontWeight:700,padding:'1px 6px',borderRadius:9999}}>{myEvenements.length}</span>}
            </div>
            <span style={{fontSize:'0.75rem',color:'#3B82F6',fontWeight:600}}>{showMyEv ? '▲ Réduire' : '▼ Voir'}</span>
          </button>
          {showMyEv && (
            <div style={{position:'absolute',top:'100%',left:0,right:0,border:'1px solid #BFDBFE',borderTop:'none',borderRadius:'0 0 10px 10px',backgroundColor:'#F8FAFF',padding:'10px 12px',display:'flex',flexDirection:'column',gap:6,boxShadow:'0 8px 24px rgba(0,0,0,0.12)'}}>
              {myEvenements.length === 0 ? (
                <div style={{fontSize:'0.8125rem',color:'#9CA3AF',textAlign:'center',padding:'8px 0'}}>Aucun événement proposé.</div>
              ) : myEvenements.map(ev => {
                const st = statusTag[ev.statut] || statusTag.draft;
                return (
                  <div key={ev.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'7px 10px',backgroundColor:'white',borderRadius:7,border:'1px solid #F3F4F6'}}>
                    <div style={{minWidth:0,flex:1}}>
                      <span style={{fontSize:'0.8125rem',fontWeight:600,color:'#111827'}}>{ev.titre}</span>
                      <span style={{marginLeft:8,fontSize:'0.75rem',color:'#9CA3AF'}}>{format(new Date(ev.date_debut),'dd MMM yyyy',{locale:fr})}</span>
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
                      <span style={{backgroundColor:st.bg,color:st.c,fontSize:'0.6875rem',fontWeight:600,padding:'2px 7px',borderRadius:4}}>{st.label}</span>
                      {(ev.statut==='draft'||ev.statut==='pending') && (
                        <button onClick={()=>handleDelete(ev.id)} style={{background:'none',border:'none',cursor:'pointer',color:'#DC2626',padding:2,display:'flex'}}>
                          <Trash2 style={{width:13,height:13}}/>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Événements groupés par mois ── */}
      {Object.keys(grouped).length === 0 ? (
        <div style={{backgroundColor:'white',border:'1px dashed #E5E7EB',borderRadius:8,padding:'3rem',textAlign:'center'}}>
          <Calendar style={{width:28,height:28,color:'#D1D5DB',margin:'0 auto 10px',display:'block'}}/>
          <p style={{color:'#9CA3AF',fontSize:'0.875rem',margin:0}}>Aucun événement programmé</p>
        </div>
      ) : (
        Object.entries(grouped).map(([month, events]) => (
          <div key={month} style={{display:'flex',flexDirection:'column',gap:6}}>
            {/* Séparateur de mois */}
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:2}}>
              <span style={{fontSize:'0.6875rem',fontWeight:700,color:'#9CA3AF',textTransform:'uppercase',letterSpacing:'0.08em',whiteSpace:'nowrap'}}>{month}</span>
              <div style={{flex:1,height:1,backgroundColor:'#E5E7EB'}}/>
            </div>

            {events.map(ev => {
              const tag = typeTag[ev.type] || typeTag.Autre;
              const d = new Date(ev.date_debut);
              const dfin = ev.date_fin ? new Date(ev.date_fin) : null;
              return (
                <div key={ev.id} onClick={()=>setSelectedEv(ev)} style={{backgroundColor:'white',border:'1px solid #E5E7EB',borderRadius:8,display:'flex',overflow:'hidden',boxShadow:'0 1px 2px rgba(0,0,0,0.04)',cursor:'pointer',transition:'box-shadow 0.15s'}} onMouseEnter={e=>e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,0.1)'} onMouseLeave={e=>e.currentTarget.style.boxShadow='0 1px 2px rgba(0,0,0,0.04)'}>
                  {/* Date sidebar */}
                  <div style={{width:52,flexShrink:0,backgroundColor:'#F9FAFB',borderRight:'1px solid #E5E7EB',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'10px 4px'}}>
                    <span style={{fontSize:'0.5625rem',fontWeight:700,color:'#9CA3AF',textTransform:'uppercase',letterSpacing:'0.05em'}}>
                      {format(d,'EEE',{locale:fr})}
                    </span>
                    <span style={{fontSize:'1.375rem',fontWeight:800,color:'#111827',lineHeight:1.1}}>
                      {format(d,'dd',{locale:fr})}
                    </span>
                  </div>

                  {/* Contenu */}
                  <div style={{padding:'10px 14px',flex:1,minWidth:0}}>
                    <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8,marginBottom:4}}>
                      <span style={{fontWeight:600,fontSize:'0.9375rem',color:'#111827',lineHeight:1.3}}>{ev.titre}</span>
                      <span style={{backgroundColor:tag.bg,color:tag.c,fontSize:'0.625rem',fontWeight:700,padding:'2px 7px',borderRadius:4,whiteSpace:'nowrap',flexShrink:0,marginTop:1}}>{ev.type||'Événement'}</span>
                    </div>

                    <div style={{display:'flex',gap:14,fontSize:'0.75rem',color:'#9CA3AF',flexWrap:'wrap'}}>
                      <span style={{display:'flex',alignItems:'center',gap:3}}>
                        <Clock style={{width:10,height:10}}/>
                        {format(d,'HH:mm')}{dfin ? ` – ${format(dfin,'HH:mm')}` : ''}
                      </span>
                      {ev.lieu && (
                        <button type="button" onClick={()=>setMapModal({lieu:ev.lieu})} style={{display:'flex',alignItems:'center',gap:3,color:'#9CA3AF',background:'none',border:'none',cursor:'pointer',padding:0,fontSize:'inherit',fontFamily:'inherit'}}>
                          <MapPin style={{width:10,height:10}}/>{ev.lieu}<ExternalLink style={{width:9,height:9,marginLeft:1,opacity:0.6}}/>
                        </button>
                      )}
                    </div>

                    {ev.description && (
                      <p style={{fontSize:'0.8125rem',color:'#6B7280',margin:'4px 0 0',overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis'}}>{ev.description}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))
      )}

      {/* ── Modal ── */}
      {showModal && (
        <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:300,padding:16}} onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div style={{backgroundColor:'white',borderRadius:14,width:'100%',maxWidth:680,boxShadow:'0 8px 40px rgba(0,0,0,0.22)',maxHeight:'92vh',overflowY:'auto',display:'flex',flexDirection:'column'}}>
            {/* Header */}
            <div style={{padding:'18px 24px 14px',borderBottom:'1px solid #F3F4F6',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <h3 style={{fontSize:'1.0625rem',fontWeight:700,margin:0,color:'#111827'}}>Proposer un événement</h3>
              <button type="button" onClick={()=>setShowModal(false)} style={{background:'none',border:'none',cursor:'pointer',color:'#9CA3AF',padding:4,display:'flex',borderRadius:6,fontSize:'1.1rem'}}>✕</button>
            </div>
            <form onSubmit={handleSubmit} style={{padding:'18px 24px 20px',display:'flex',flexDirection:'column',gap:14}}>
              {/* Type */}
              <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                {['QSO','Stamm','Concours','Assemblée','Autre'].map(t=>{
                  const active=formData.type===t; const tag=typeTag[t]||typeTag.Autre;
                  return <button key={t} type="button" onClick={()=>setFormData({...formData,type:t})} style={{padding:'5px 12px',borderRadius:6,fontSize:'0.8125rem',fontWeight:600,cursor:'pointer',border:'1px solid',backgroundColor:active?tag.bg:'white',color:active?tag.c:'#6B7280',borderColor:active?tag.c:'#E5E7EB'}}>{t}</button>;
                })}
              </div>
              {/* Titre */}
              <div>
                <label style={{display:'block',fontSize:'0.75rem',fontWeight:600,color:'#374151',marginBottom:4}}>Titre *</label>
                <input required style={inp} value={formData.titre} onChange={e=>setFormData({...formData,titre:e.target.value})} placeholder="Ex: Assemblée générale"/>
              </div>
              {/* Description */}
              <div>
                <label style={{display:'block',fontSize:'0.75rem',fontWeight:600,color:'#374151',marginBottom:4}}>Description *</label>
                <textarea required style={{...inp,minHeight:90,resize:'vertical'}} value={formData.description} onChange={e=>setFormData({...formData,description:e.target.value})} placeholder="Programme, infos pratiques…"/>
              </div>
              {/* Début */}
              <div>
                <label style={{display:'block',fontSize:'0.75rem',fontWeight:600,color:'#374151',marginBottom:4}}>Début *</label>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                  <input required type="date" style={inp} value={formData.debut_date} onChange={e=>setFormData({...formData,debut_date:e.target.value})} placeholder="jj/mm/aaaa"/>
                  <input required type="time" style={inp} value={formData.debut_heure} onChange={e=>setFormData({...formData,debut_heure:e.target.value})}/>
                </div>
              </div>
              {/* Fin */}
              <div>
                <label style={{display:'block',fontSize:'0.75rem',fontWeight:600,color:'#374151',marginBottom:4}}>Fin <span style={{fontWeight:400,color:'#9CA3AF'}}>(optionnel)</span></label>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                  <input type="date" style={inp} value={formData.fin_date} onChange={e=>setFormData({...formData,fin_date:e.target.value})}/>
                  <input type="time" style={inp} value={formData.fin_heure} onChange={e=>setFormData({...formData,fin_heure:e.target.value})}/>
                </div>
              </div>
              {/* Lieu + lien Maps */}
              <div>
                <label style={{display:'block',fontSize:'0.75rem',fontWeight:600,color:'#374151',marginBottom:4}}>Lieu *</label>
                <input required style={inp} value={formData.lieu} onChange={e=>setFormData({...formData,lieu:e.target.value})} placeholder="Local du club, Genève"/>
                {formData.lieu && (
                  <button type="button" onClick={()=>setMapModal({lieu:formData.lieu})}
                    style={{display:'inline-flex',alignItems:'center',gap:3,fontSize:'0.6875rem',color:'#2563EB',background:'none',border:'none',cursor:'pointer',padding:0,fontWeight:500,marginTop:4}}>
                    <MapPin style={{width:11,height:11}}/> Voir sur Google Maps <ExternalLink style={{width:10,height:10}}/>
                  </button>
                )}
              </div>
              {/* Footer */}
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,marginTop:2,paddingTop:12,borderTop:'1px solid #F3F4F6'}}>
                <p style={{fontSize:'0.75rem',color:'#9CA3AF',margin:0}}>Soumis au comité pour validation avant publication.</p>
                <div style={{display:'flex',gap:8,flexShrink:0}}>
                  <button type="button" onClick={()=>setShowModal(false)} style={{padding:'8px 16px',borderRadius:7,border:'1px solid #E5E7EB',background:'white',cursor:'pointer',fontSize:'0.875rem',color:'#374151'}}>Annuler</button>
                  <button type="submit" style={{padding:'8px 18px',borderRadius:7,backgroundColor:'#111827',color:'white',border:'none',cursor:'pointer',fontSize:'0.875rem',fontWeight:700}}>Soumettre</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal carte Google Maps ── */}
      {mapModal && (
        <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:400,padding:16}} onClick={e=>e.target===e.currentTarget&&setMapModal(null)}>
          <div style={{backgroundColor:'white',borderRadius:12,width:'100%',maxWidth:680,boxShadow:'0 8px 40px rgba(0,0,0,0.25)',overflow:'hidden',display:'flex',flexDirection:'column'}}>
            <div style={{padding:'12px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',borderBottom:'1px solid #E5E7EB'}}>
              <div style={{display:'flex',alignItems:'center',gap:6}}>
                <MapPin style={{width:14,height:14,color:'#2563EB'}}/>
                <span style={{fontWeight:600,fontSize:'0.875rem',color:'#111827'}}>{mapModal.lieu}</span>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <a href={`https://maps.google.com/?q=${encodeURIComponent(mapModal.lieu)}`} target="_blank" rel="noopener noreferrer"
                  style={{display:'inline-flex',alignItems:'center',gap:4,fontSize:'0.75rem',color:'#2563EB',textDecoration:'none',fontWeight:500}}>
                  Ouvrir dans Maps <ExternalLink style={{width:11,height:11}}/>
                </a>
                <button onClick={()=>setMapModal(null)} style={{background:'none',border:'none',cursor:'pointer',color:'#9CA3AF',padding:4,display:'flex',borderRadius:6,fontSize:'1.1rem'}}>✕</button>
              </div>
            </div>
            <iframe
              title="Google Maps"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(mapModal.lieu)}&output=embed&hl=fr`}
              width="100%"
              height="380"
              style={{border:'none',display:'block'}}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      )}

      {/* ── Modale détail événement ── */}
      {selectedEv && (() => {
        const ev = selectedEv;
        const tag = typeTag[ev.type] || typeTag.Autre;
        const d = new Date(ev.date_debut);
        const dfin = ev.date_fin ? new Date(ev.date_fin) : null;
        const isMobile = window.innerWidth < 768;
        return (
          <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:500,padding:isMobile?'16px':'16px'}} onClick={()=>setSelectedEv(null)}>
            <div style={{backgroundColor:'white',borderRadius:'14px',width:'100%',maxWidth:640,maxHeight:'90vh',overflowY:'auto',boxShadow:'0 8px 40px rgba(0,0,0,0.2)',display:'flex',flexDirection:'column'}} onClick={e=>e.stopPropagation()}>
              {/* Handle + header */}
              <div style={{padding:'12px 20px 0',display:'flex',flexDirection:'column',alignItems:'center',gap:12}}>
                <div style={{width:36,height:4,borderRadius:2,backgroundColor:'#E5E7EB'}}/>
                <div style={{width:'100%',display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:4,flexWrap:'wrap'}}>
                      <span style={{backgroundColor:tag.bg,color:tag.c,fontSize:'0.625rem',fontWeight:700,padding:'2px 7px',borderRadius:4}}>{ev.type||'Événement'}</span>
                    </div>
                    <h3 style={{fontWeight:800,fontSize:'1.0625rem',color:'#111827',margin:0,lineHeight:1.3}}>{ev.titre}</h3>
                  </div>
                  <button onClick={()=>setSelectedEv(null)} style={{background:'none',border:'none',cursor:'pointer',color:'#9CA3AF',padding:4,display:'flex',borderRadius:6,fontSize:'1.2rem',flexShrink:0}}>✕</button>
                </div>
              </div>
              {/* Corps */}
              <div style={{padding:'16px 20px 28px',display:'flex',flexDirection:'column',gap:14}}>
                {/* Date & heure */}
                <div style={{display:'flex',alignItems:'center',gap:8,padding:'10px 14px',backgroundColor:'#F9FAFB',borderRadius:8}}>
                  <Calendar style={{width:15,height:15,color:'#6B7280',flexShrink:0}}/>
                  <div>
                    <div style={{fontWeight:700,fontSize:'0.875rem',color:'#111827',textTransform:'capitalize'}}>
                      {format(d,'EEEE d MMMM yyyy',{locale:fr})}
                    </div>
                    <div style={{fontSize:'0.8125rem',color:'#6B7280',marginTop:1,display:'flex',alignItems:'center',gap:3}}>
                      <Clock style={{width:11,height:11}}/>
                      {format(d,'HH:mm')}{dfin ? ` – ${format(dfin,'HH:mm')}` : ''}
                    </div>
                  </div>
                </div>
                {/* Lieu */}
                {ev.lieu && (
                  <button type="button" onClick={()=>{setSelectedEv(null);setMapModal({lieu:ev.lieu});}}
                    style={{display:'flex',alignItems:'center',gap:8,padding:'10px 14px',backgroundColor:'#F9FAFB',borderRadius:8,border:'none',cursor:'pointer',textAlign:'left',width:'100%'}}>
                    <MapPin style={{width:15,height:15,color:'#2563EB',flexShrink:0}}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:'0.875rem',color:'#111827',fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ev.lieu}</div>
                      <div style={{fontSize:'0.75rem',color:'#2563EB',marginTop:1}}>Voir sur la carte →</div>
                    </div>
                  </button>
                )}
                {/* Description */}
                {ev.description && (
                  <div>
                    <div style={{fontSize:'0.75rem',fontWeight:700,color:'#6B7280',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6}}>Description</div>
                    <p style={{fontSize:'0.875rem',color:'#374151',lineHeight:1.6,margin:0,whiteSpace:'pre-wrap',wordBreak:'break-word'}}>{ev.description}</p>
                  </div>
                )}
                {/* Lien externe */}
                {ev.lien && (
                  <a href={ev.lien} target="_blank" rel="noopener noreferrer"
                    style={{display:'inline-flex',alignItems:'center',gap:6,padding:'8px 14px',backgroundColor:'#EFF6FF',color:'#2563EB',borderRadius:8,fontSize:'0.875rem',fontWeight:600,textDecoration:'none'}}>
                    <ExternalLink style={{width:13,height:13}}/> En savoir plus
                  </a>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Bandeau connexion requise ── */}
      {showLoginBanner && (
        <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:500,padding:16}} onClick={()=>setShowLoginBanner(false)}>
          <div style={{backgroundColor:'white',borderRadius:14,width:'100%',maxWidth:380,padding:'28px 24px',boxShadow:'0 8px 40px rgba(0,0,0,0.2)',textAlign:'center'}} onClick={e=>e.stopPropagation()}>
            <div style={{width:48,height:48,borderRadius:'50%',backgroundColor:'#F3F4F6',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 16px'}}>
              <Lock style={{width:22,height:22,color:'#374151'}}/>
            </div>
            <h3 style={{fontWeight:800,fontSize:'1.0625rem',color:'#111827',margin:'0 0 8px'}}>Connexion requise</h3>
            <p style={{fontSize:'0.875rem',color:'#6B7280',margin:'0 0 20px',lineHeight:1.5}}>Vous devez être connecté pour proposer un événement. Créez un compte ou connectez-vous pour accéder à cette fonctionnalité.</p>
            <div style={{display:'flex',gap:10,justifyContent:'center'}}>
              <button onClick={()=>setShowLoginBanner(false)} style={{padding:'9px 18px',borderRadius:8,border:'1px solid #E5E7EB',background:'white',cursor:'pointer',fontSize:'0.875rem',color:'#374151',fontWeight:600}}>Annuler</button>
              <button onClick={()=>navigate('/auth')} style={{padding:'9px 18px',borderRadius:8,backgroundColor:'#111827',color:'white',border:'none',cursor:'pointer',fontSize:'0.875rem',fontWeight:700}}>Se connecter</button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={()=>setToast(null)}/>}
    </div>
  );
};

export default AgendaPage;
